import { cn } from 'shadcn/lib/utils';

export const SCENARIOS = ['prod', 'dev', 'demo', 'test'] as const;
export type Scenario = (typeof SCENARIOS)[number];

export const SCENARIO_RANK: Record<Scenario, number> = {
  prod: 0,
  dev: 1,
  demo: 2,
  test: 3
};

const SCENARIO_CONFIG: Record<
  Scenario,
  { label: string; className: string; frameColor: string | null }
> = {
  prod: {
    label: 'Prod',
    className: 'bg-red-100 text-red-700 border-red-200',
    // Prod intentionally has no screen frame.
    frameColor: null
  },
  dev: {
    label: 'Dev',
    className: 'bg-blue-100 text-blue-700 border-blue-200',
    frameColor: '#2563eb'
  },
  demo: {
    label: 'Demo',
    className: 'bg-purple-100 text-purple-700 border-purple-200',
    frameColor: '#9333ea'
  },
  test: {
    label: 'Test',
    className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    frameColor: '#059669'
  }
};

const isScenario = (value: string | null | undefined): value is Scenario =>
  value === 'prod' || value === 'dev' || value === 'demo' || value === 'test';

// The color used for the full-screen frame of a given organization type.
// Returns `null` when the type should not show a frame (prod or unknown).
export const scenarioFrameColor = (
  type: string | null | undefined
): string | null =>
  isScenario(type) ? SCENARIO_CONFIG[type].frameColor : null;

type Props = {
  type: string | null | undefined;
  className?: string;
};

export const ScenarioBadge = ({ type, className }: Props) => {
  if (!isScenario(type)) {
    return null;
  }
  const config = SCENARIO_CONFIG[type];
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-md border px-1.5 py-0 font-medium text-[10px] leading-4',
        config.className,
        className
      )}
      data-testid="scenario-badge"
    >
      {config.label}
    </span>
  );
};
